import {
  CognitoUserPool,
  CognitoUserAttribute,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  ISignUpResult
} from "amazon-cognito-identity-js";
import { awsConfig } from "./aws-config";

// Initialize user pool
export const userPool = new CognitoUserPool({
  UserPoolId: awsConfig.UserPoolId,
  ClientId: awsConfig.ClientId,
});

export const signUp = (
  username: string,
  password: string,
  attributes: { [key: string]: string },
  callback: (err: Error | undefined, result: ISignUpResult | undefined) => void
) => {
  const attributeList = Object.entries(attributes).map(
    ([Name, Value]) => new CognitoUserAttribute({ Name, Value })
  );

  userPool.signUp(username, password, attributeList, [], callback);
};

// Resend verification code
export const resendVerificationCode = (
  username: string,
  callback: (err: Error | undefined, result: any | undefined) => void
): void => {
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });
  console.log("Resending verification code to user:", username);

  cognitoUser.resendConfirmationCode(callback);
};

// Typed result for sign-in
export type SignInResult =
  | {
    status: "SUCCESS";
    session: CognitoUserSession;
    cognitoUser: CognitoUser;
  }
  | {
    status: "NEW_PASSWORD_REQUIRED";
    userAttributes: any;
    requiredAttributes: any;
    cognitoUser: CognitoUser;
  }
  | {
    status: "CHALLENGE";
    challengeName: string;
    challengeParameters: any;
    cognitoUser: CognitoUser;
  }
  | {
    status: "ERROR";
    error: any;
  };


// Sign in a user
export const signIn = (
  username: string,
  password: string,
  callback: (result: SignInResult) => void
) => {
  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });

  cognitoUser.authenticateUser(authenticationDetails, {
    onSuccess: (session) => {
      callback({ status: "SUCCESS", session, cognitoUser });
    },

    onFailure: (error) => {
      callback({ status: "ERROR", error });
    },

    newPasswordRequired: (userAttributes, requiredAttributes) => {
      callback({
        status: "NEW_PASSWORD_REQUIRED",
        userAttributes,
        requiredAttributes,
        cognitoUser,
      });
    },

    mfaRequired: (challengeName, challengeParameters) => {
      callback({
        status: "CHALLENGE",
        challengeName,
        challengeParameters,
        cognitoUser,
      });
    },

    totpRequired: (challengeName, challengeParameters) => {
      callback({
        status: "CHALLENGE",
        challengeName,
        challengeParameters,
        cognitoUser,
      });
    },

    customChallenge: (challengeParameters) => {
      callback({
        status: "CHALLENGE",
        challengeName: "CUSTOM_CHALLENGE",
        challengeParameters,
        cognitoUser,
      });
    },

    mfaSetup: (challengeName, challengeParameters) => {
      callback({
        status: "CHALLENGE",
        challengeName,
        challengeParameters,
        cognitoUser,
      });
    },

    selectMFAType: (challengeName, challengeParameters) => {
      callback({
        status: "CHALLENGE",
        challengeName,
        challengeParameters,
        cognitoUser,
      });
    },
  });
};

// Complete new password challenge
export const setNewPassword = (
  cognitoUser: CognitoUser,
  newPassword: string
): Promise<boolean> => {
  return new Promise<boolean>((resolve, reject) => {
    if (!cognitoUser) {
      reject(new Error("Invalid Cognito user"));
      return;
    }

    cognitoUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: () => {
          resolve(true);
        },
        onFailure: (err) => {
          reject(err);
        },
      }
    );
  });
};

// Forgot password (send code)
export const forgotPassword = (
  username: string,
  callback: (err: any, result: any) => void
) => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  cognitoUser.forgotPassword({
    onSuccess: (result) => callback(null, result),
    onFailure: (err) => callback(err, null),
  });
};

// Confirm forgot password with code
export const confirmationCode = (
  username: string,
  verificationCode: string,
  newPassword: string,
  callback: (err: any, result: any) => void
) => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  cognitoUser.confirmPassword(verificationCode, newPassword, {
    onSuccess: (result) => callback(null, result),
    onFailure: (err) => callback(err, null),
  });
};

// Change password
export const changePassword = (
  oldPassword: string,
  newPassword: string,
  callback: (err: any, result: any) => void
) => {
  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) {
    callback(new Error("No user currently signed in."), null);
    return;
  }

  cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
    if (err || !session) {
      callback(err ?? new Error("No session found"), null);
      return;
    }

    cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
      callback(err, result);
    });
  });
};

// Update user attributes
export const updateUserInfo = (
  username: string,
  attributes: { [key: string]: string },
  callback: (err: any, result: any) => void
) => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });

  const attributeList = Object.entries(attributes).map(
    ([Name, Value]) => new CognitoUserAttribute({ Name, Value })
  );

  cognitoUser.updateAttributes(attributeList, (err, result) => {
    callback(err, result);
  });
};

// Sign out user
export const signOut = () => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
    console.log(`User ${cognitoUser.getUsername()} signed out.`);
  } else {
    console.log("No user currently signed in.");
  }
};

// Confirm sign-up with verification code
export const confirmSignUp = (
  username: string,
  code: string,
  callback: (err: any, result: any) => void
) => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  cognitoUser.confirmRegistration(code, true, callback);
};

// Refresh session tokens
export const refreshTokens = (
  callback: (err: any, result: { idToken: string; accessToken: string; refreshToken: string } | null) => void
) => {
  const cognitoUser = userPool.getCurrentUser();

  if (!cognitoUser) {
    callback("No current user found", null);
    return;
  }

  cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
    if (err || !session) {
      callback(err ?? "No session found", null);
      return;
    }

    const refreshToken = session.getRefreshToken();
    if (!refreshToken) {
      callback("No refresh token found", null);
      return;
    }

    cognitoUser.refreshSession(refreshToken, (err, newSession) => {
      if (err) {
        callback(err, null);
      } else {
        callback(null, {
          idToken: newSession.getIdToken().getJwtToken(),
          accessToken: newSession.getAccessToken().getJwtToken(),
          refreshToken: newSession.getRefreshToken().getToken(),
        });
      }
    });
  });
};